// src/components/VirusTotalPanel.js
// Drop-in replacement for the "Threat Intelligence Enrichment" section in IOCView
// Usage: <VirusTotalPanel ioc={selectedIOC} />

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import {
  Shield, AlertTriangle, CheckCircle, HelpCircle, ExternalLink,
  RefreshCw, Loader2, Globe, Server, FileText, Hash, Clock,
  ChevronDown, ChevronUp, XCircle, AlertCircle, Info
} from 'lucide-react';

// IOC types that VirusTotal can handle
const VT_SUPPORTED_TYPES = [
  'IP Address',
  'Domain',
  'URL',
  'File Hash (MD5)',
  'File Hash (SHA-1)',
  'File Hash (SHA-256)'
];

// Verdict styling
const VERDICT_CONFIG = {
  malicious: {
    label: 'Malicious',
    icon: XCircle,
    bg: 'bg-red-500/10',
    border: 'border-red-500/30',
    text: 'text-red-400',
    badge: 'bg-red-500/20 text-red-300 border-red-500/30',
    barColor: 'bg-red-500'
  },
  suspicious: {
    label: 'Suspicious',
    icon: AlertCircle,
    bg: 'bg-orange-500/10',
    border: 'border-orange-500/30',
    text: 'text-orange-400',
    badge: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
    barColor: 'bg-orange-500'
  },
  clean: {
    label: 'Clean',
    icon: CheckCircle,
    bg: 'bg-green-500/10',
    border: 'border-green-500/30',
    text: 'text-green-400',
    badge: 'bg-green-500/20 text-green-300 border-green-500/30',
    barColor: 'bg-green-500'
  }
};

// Donut-style detection ratio display
const DetectionRing = ({ malicious, suspicious, total }) => {
  const flagged = malicious + suspicious;
  const percentage = total > 0 ? Math.round((flagged / total) * 100) : 0;
  const isClean = flagged === 0;

  return (
    <div className="flex items-center gap-4">
      {/* Ring */}
      <div className="relative w-20 h-20 shrink-0">
        <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
          <circle cx="40" cy="40" r="30" fill="none" stroke="#1e293b" strokeWidth="10" />
          {total > 0 && (
            <circle
              cx="40" cy="40" r="30"
              fill="none"
              stroke={isClean ? '#22c55e' : malicious > 0 ? '#ef4444' : '#f97316'}
              strokeWidth="10"
              strokeDasharray={`${(flagged / total) * 188.5} 188.5`}
              strokeLinecap="round"
            />
          )}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-lg font-bold leading-none ${isClean ? 'text-green-400' : malicious > 0 ? 'text-red-400' : 'text-orange-400'}`}>
            {flagged}
          </span>
          <span className="text-xs text-slate-500 leading-none mt-0.5">/{total}</span>
        </div>
      </div>

      {/* Stats */}
      <div className="flex-1 space-y-1.5 text-sm">
        <div className="flex justify-between">
          <span className="text-slate-400">Malicious</span>
          <span className="text-red-400 font-semibold">{malicious}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-400">Suspicious</span>
          <span className="text-orange-400 font-semibold">{suspicious}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-400">Clean</span>
          <span className="text-green-400 font-semibold">{total - flagged}</span>
        </div>
        <div className="flex justify-between border-t border-slate-700/50 pt-1.5 mt-1.5">
          <span className="text-slate-400">Total engines</span>
          <span className="text-slate-200 font-semibold">{total}</span>
        </div>
      </div>
    </div>
  );
};

// Individual vendor detection row
const DetectionRow = ({ vendor, category, result }) => {
  const isMalicious = category === 'malicious';
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-slate-800/50 last:border-0">
      <span className="text-xs text-slate-300 font-medium">{vendor}</span>
      <span className={`text-xs px-2 py-0.5 rounded border ${
        isMalicious
          ? 'bg-red-500/15 text-red-300 border-red-500/25'
          : 'bg-orange-500/15 text-orange-300 border-orange-500/25'
      }`}>
        {result}
      </span>
    </div>
  );
};

// IP/Domain/File metadata display
const MetaField = ({ label, value, mono = false }) => {
  if (!value && value !== 0) return null;
  return (
    <div className="flex justify-between items-start gap-4 py-1.5 border-b border-slate-800/40 last:border-0">
      <span className="text-xs text-slate-500 shrink-0">{label}</span>
      <span className={`text-xs text-slate-300 text-right break-all ${mono ? 'font-mono' : ''}`}>
        {value}
      </span>
    </div>
  );
};

const VirusTotalPanel = ({ ioc }) => {
  const { getToken } = useAuth();
  const [vtData, setVtData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAllDetections, setShowAllDetections] = useState(false);
  const [lastLookedUp, setLastLookedUp] = useState(null);

  const isSupported = ioc && VT_SUPPORTED_TYPES.includes(ioc.ioc_type);

  // Auto-lookup when IOC changes (only if supported type)
  useEffect(() => {
    if (ioc && isSupported && ioc.ioc_value !== lastLookedUp) {
      // Small debounce to avoid double-firing
      const timer = setTimeout(() => runLookup(), 300);
      return () => clearTimeout(timer);
    } else if (ioc?.ioc_value !== lastLookedUp) {
      // Reset if unsupported type selected
      setVtData(null);
      setError(null);
    }
  }, [ioc?.ioc_value, ioc?.ioc_type]);

  const runLookup = useCallback(async () => {
    if (!ioc || !isSupported) return;
    setLoading(true);
    setError(null);
    setVtData(null);

    try {
      const token = await getToken();
      const response = await fetch('/api/virustotal/lookup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          indicator: ioc.ioc_value,
          ioc_type: ioc.ioc_type
        })
      });

      const data = await response.json();

      if (response.status === 429) {
        setError({
          type: 'rate_limit',
          message: `Rate limit reached. Retry in ${data.retry_after_seconds || 60}s`,
          retryAfter: data.retry_after_seconds || 60
        });
        return;
      }

      if (response.status === 503 && data.setup_required) {
        setError({ type: 'setup', message: 'VirusTotal API key not configured on server.' });
        return;
      }

      if (!response.ok) {
        setError({ type: 'error', message: data.error || 'Lookup failed' });
        return;
      }

      setVtData(data);
      setLastLookedUp(ioc.ioc_value);
    } catch (err) {
      setError({ type: 'error', message: 'Network error — could not reach server' });
    } finally {
      setLoading(false);
    }
  }, [ioc, getToken, isSupported]);

  // ─── Unsupported type ───────────────────────────────────────────────────────
  if (!isSupported) {
    return (
      <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-lg bg-slate-700/50 flex items-center justify-center">
            <Shield className="w-4 h-4 text-slate-400" />
          </div>
          <h3 className="text-base font-semibold text-slate-100">VirusTotal Lookup</h3>
          <span className="ml-auto text-xs text-slate-600">Powered by VirusTotal</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Info className="w-4 h-4 shrink-0" />
          <span>
            VT lookup is available for IP addresses, domains, URLs, and file hashes.
            {ioc?.ioc_type && ` "${ioc.ioc_type}" is not supported.`}
          </span>
        </div>
      </div>
    );
  }

  // ─── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
            <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
          </div>
          <h3 className="text-base font-semibold text-slate-100">VirusTotal Lookup</h3>
          <span className="ml-auto text-xs text-slate-600">Powered by VirusTotal</span>
        </div>
        <div className="space-y-2.5">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-3 bg-slate-700/40 rounded animate-pulse" style={{ width: `${70 + i * 7}%` }} />
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-3">Querying VirusTotal intelligence...</p>
      </div>
    );
  }

  // ─── Error states ───────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center">
            <AlertTriangle className="w-4 h-4 text-orange-400" />
          </div>
          <h3 className="text-base font-semibold text-slate-100">VirusTotal Lookup</h3>
          <span className="ml-auto text-xs text-slate-600">Powered by VirusTotal</span>
        </div>

        {error.type === 'setup' && (
          <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-sm">
            <p className="text-yellow-300 font-medium mb-1">API Key Required</p>
            <p className="text-yellow-400/70 text-xs">
              Add <code className="bg-slate-900/60 px-1 rounded">VIRUSTOTAL_API_KEY=your_key</code> to your server <code className="bg-slate-900/60 px-1 rounded">.env</code> file.
              Free keys available at virustotal.com
            </p>
          </div>
        )}

        {error.type === 'rate_limit' && (
          <div className="p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg text-sm">
            <p className="text-orange-300 font-medium mb-1">Rate Limited (Free Tier)</p>
            <p className="text-orange-400/70 text-xs">{error.message}</p>
          </div>
        )}

        {error.type === 'error' && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm">
            <p className="text-red-300 font-medium mb-1">Lookup Failed</p>
            <p className="text-red-400/70 text-xs">{error.message}</p>
          </div>
        )}

        <button
          onClick={runLookup}
          className="mt-3 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-xs text-slate-200 transition-all flex items-center gap-1.5"
        >
          <RefreshCw className="w-3 h-3" /> Retry
        </button>
      </div>
    );
  }

  // ─── Not found ──────────────────────────────────────────────────────────────
  if (vtData && !vtData.found) {
    return (
      <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center">
            <CheckCircle className="w-4 h-4 text-green-400" />
          </div>
          <h3 className="text-base font-semibold text-slate-100">VirusTotal Lookup</h3>
          <span className="ml-auto text-xs text-slate-600">Powered by VirusTotal</span>
        </div>
        <p className="text-sm text-slate-400">Not found in VirusTotal database — no prior submissions or detections.</p>
        <button
          onClick={runLookup}
          className="mt-3 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-xs text-slate-200 transition-all flex items-center gap-1.5"
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>
    );
  }

  // ─── No data yet (pre-lookup) ────────────────────────────────────────────────
  if (!vtData) {
    return (
      <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
            <Shield className="w-4 h-4 text-blue-400" />
          </div>
          <h3 className="text-base font-semibold text-slate-100">VirusTotal Lookup</h3>
          <span className="ml-auto text-xs text-slate-600">Powered by VirusTotal</span>
        </div>
        <button
          onClick={runLookup}
          className="px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 rounded-lg text-sm text-blue-400 transition-all flex items-center gap-2"
        >
          <Shield className="w-4 h-4" /> Run VirusTotal Lookup
        </button>
      </div>
    );
  }

  // ─── Full results ────────────────────────────────────────────────────────────
  const verdictCfg = VERDICT_CONFIG[vtData.verdict] || VERDICT_CONFIG.clean;
  const VerdictIcon = verdictCfg.icon;
  const { analysis_stats: as, top_detections } = vtData;
  const displayedDetections = showAllDetections ? top_detections : top_detections.slice(0, 5);

  return (
    <div className={`rounded-lg border p-5 ${verdictCfg.bg} ${verdictCfg.border}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${verdictCfg.bg}`}>
            <VerdictIcon className={`w-4 h-4 ${verdictCfg.text}`} />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-100">VirusTotal</h3>
            {vtData.last_analysis_date && (
              <p className="text-xs text-slate-500 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Last scanned {new Date(vtData.last_analysis_date).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Verdict badge */}
          <span className={`px-3 py-1 rounded-full text-xs font-bold border uppercase tracking-wide ${verdictCfg.badge}`}>
            {verdictCfg.label}
          </span>

          {/* VT link */}
          <a
            href={vtData.vt_link}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 bg-slate-700/50 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-slate-200 transition-all"
            title="View on VirusTotal"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </a>

          {/* Refresh */}
          <button
            onClick={runLookup}
            className="p-1.5 bg-slate-700/50 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-slate-200 transition-all"
            title="Refresh lookup"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Detection ring + stats */}
      <div className="mb-4 p-4 bg-slate-900/40 rounded-lg">
        <DetectionRing
          malicious={as.malicious}
          suspicious={as.suspicious}
          total={as.total}
        />
      </div>

      {/* Threat Score Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-xs mb-1">
          <span className="text-slate-400">Threat Score</span>
          <span className={verdictCfg.text + ' font-semibold'}>{vtData.threat_score}/100</span>
        </div>
        <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${verdictCfg.barColor}`}
            style={{ width: `${vtData.threat_score}%` }}
          />
        </div>
      </div>

      {/* Type-specific metadata */}
      {vtData.ioc_type === 'IP Address' && (
        <div className="mb-4 p-3 bg-slate-900/40 rounded-lg">
          <p className="text-xs font-medium text-slate-400 mb-2 flex items-center gap-1.5">
            <Server className="w-3 h-3" /> IP Intelligence
          </p>
          <MetaField label="Country" value={vtData.country} />
          <MetaField label="ASN" value={vtData.asn ? `AS${vtData.asn}` : null} mono />
          <MetaField label="AS Owner" value={vtData.as_owner} />
          <MetaField label="Network" value={vtData.network} mono />
          <MetaField label="RIR" value={vtData.regional_internet_registry} />
          <MetaField label="Reputation" value={vtData.reputation !== undefined ? vtData.reputation : null} />
        </div>
      )}

      {vtData.ioc_type === 'Domain' && (
        <div className="mb-4 p-3 bg-slate-900/40 rounded-lg">
          <p className="text-xs font-medium text-slate-400 mb-2 flex items-center gap-1.5">
            <Globe className="w-3 h-3" /> Domain Intelligence
          </p>
          <MetaField label="Registrar" value={vtData.registrar} />
          <MetaField label="TLD" value={vtData.tld} mono />
          <MetaField label="Reputation" value={vtData.reputation !== undefined ? vtData.reputation : null} />
          {vtData.dns_records_a?.length > 0 && (
            <div className="py-1.5 border-b border-slate-800/40">
              <span className="text-xs text-slate-500">Resolves to</span>
              <div className="mt-1 flex flex-wrap gap-1">
                {vtData.dns_records_a.map((ip, i) => (
                  <span key={i} className="text-xs font-mono bg-slate-800 text-slate-300 px-1.5 py-0.5 rounded">{ip}</span>
                ))}
              </div>
            </div>
          )}
          {Object.keys(vtData.categories || {}).length > 0 && (
            <div className="py-1.5">
              <span className="text-xs text-slate-500 block mb-1">Categories</span>
              <div className="flex flex-wrap gap-1">
                {Object.values(vtData.categories).map((cat, i) => (
                  <span key={i} className="text-xs bg-purple-500/20 text-purple-300 px-1.5 py-0.5 rounded border border-purple-500/20">
                    {cat}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {(vtData.ioc_type?.includes('Hash') || vtData.ioc_type?.includes('SHA') || vtData.ioc_type?.includes('MD5')) && (
        <div className="mb-4 p-3 bg-slate-900/40 rounded-lg">
          <p className="text-xs font-medium text-slate-400 mb-2 flex items-center gap-1.5">
            <FileText className="w-3 h-3" /> File Intelligence
          </p>
          <MetaField label="File Name" value={vtData.file_name} />
          <MetaField label="File Type" value={vtData.file_type} />
          <MetaField label="File Size" value={vtData.file_size_human} />
          <MetaField label="Threat Name" value={vtData.popular_threat_name} />
          <MetaField label="Times Submitted" value={vtData.times_submitted} />
          {vtData.md5 && <MetaField label="MD5" value={vtData.md5} mono />}
          {vtData.sha1 && <MetaField label="SHA-1" value={vtData.sha1} mono />}
          {vtData.sha256 && <MetaField label="SHA-256" value={vtData.sha256} mono />}
          {vtData.tags?.length > 0 && (
            <div className="py-1.5">
              <span className="text-xs text-slate-500 block mb-1">Tags</span>
              <div className="flex flex-wrap gap-1">
                {vtData.tags.map((tag, i) => (
                  <span key={i} className="text-xs bg-slate-800 text-slate-300 px-1.5 py-0.5 rounded">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Vendor detections */}
      {top_detections.length > 0 && (
        <div className="p-3 bg-slate-900/40 rounded-lg">
          <p className="text-xs font-medium text-slate-400 mb-2">
            Flagged by {top_detections.length} vendors
          </p>
          {displayedDetections.map((det, i) => (
            <DetectionRow key={i} {...det} />
          ))}
          {top_detections.length > 5 && (
            <button
              onClick={() => setShowAllDetections(v => !v)}
              className="mt-2 w-full py-1.5 text-xs text-slate-400 hover:text-slate-200 transition-all flex items-center justify-center gap-1"
            >
              {showAllDetections ? (
                <><ChevronUp className="w-3 h-3" /> Show fewer</>
              ) : (
                <><ChevronDown className="w-3 h-3" /> Show all {top_detections.length} detections</>
              )}
            </button>
          )}
        </div>
      )}

      {/* Footer attribution */}
      <div className="mt-3 pt-2 border-t border-slate-700/30 flex items-center justify-between">
        <span className="text-xs text-slate-600">Powered by VirusTotal</span>
        <a
          href={vtData.vt_link}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-slate-500 hover:text-blue-400 transition-colors flex items-center gap-1"
        >
          Full report <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
};

export default VirusTotalPanel;